import { UITable } from '../../components/index';
import { UIDynamicTable } from '../../components/table/ui-dynamic-table';
import type { ComponentPlugin } from '../../components/ui-element.type';
import {
    closestEventHolder,
    setAttributes,
    updateClassList,
    updateElement,
} from '../../global/ui-helpers';
import type {
    UISortablePluginSortDirection,
    UISortablePluginSortEventDetail,
    UISortablePluginSortParams,
} from './ui-sortable-plugin.type';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for ui-dynamic-table sortable plugin.
 * @alias UISortablePlugin
 * @implements {ComponentPlugin}
 * @fires event:sort-table
 * @param {UIDynamicTable} target Target instance.
 * @property {UIDynamicTable} target Target instance.
 */
export class UISortablePlugin implements ComponentPlugin {
    static readonly Direction = {
        Ascending: 1,
        Descending: -1,
    } as const;

    private readonly target: UIDynamicTable;

    constructor(target: UIDynamicTable) {
        this.target = target;
    }

    /**
     * @inheritDoc
     */
    modifyTable(uiTable: UITable): UITable {
        const isAsc = this.target.sortDir === 'asc';
        const order = this.target.sortOrder;
        const hasNoDefaultOrder = !order;

        uiTable.querySelectorAll('th[data-sort-column]').forEach((th) => {
            const targetOrder = (th as HTMLElement).dataset.sortColumn;
            const isSameOrder = order && order === targetOrder;
            updateElement(th, {
                classList: {
                    'sort': true,
                    '-asc': hasNoDefaultOrder || isAsc,
                    '-desc': !isAsc,
                    '-sorted': !!isSameOrder,
                },
                attributes: {
                    'data-event': 'sort',
                },
                children: [
                    {
                        tagName: 'button',
                        attributes: { type: 'button' },
                        children: [...th.childNodes],
                    },
                ],
            });
        });
        return uiTable;
    }

    /**
     * Apply sorting by given column
     * @fires event:sort-table
     * @param {HTMLTableCellElement} th
     */
    sortByColumn(th: HTMLTableCellElement): void {
        if (!th?.parentElement) {
            return;
        }

        let direction: UISortablePluginSortDirection;
        if (th.classList.contains('-sorted')) {
            direction = th.classList.contains('-desc')
                ? UISortablePlugin.Direction.Ascending
                : UISortablePlugin.Direction.Descending;
        } else {
            direction = UISortablePlugin.Direction.Descending;
        }

        th.parentElement
            .querySelectorAll('th.sort')
            .forEach((node) =>
                this.updateHeaders(node as HTMLTableCellElement, th, direction)
            );

        this.target.dispatchCustomEvent('sort-table', {
            direction,
            column: th.dataset.sortColumn!,
        } satisfies UISortablePluginSortEventDetail);
    }

    /**
     * @param {UISortablePluginSortParams} params
     */
    setSortParams(params: UISortablePluginSortParams): void {
        if (!params.field) {
            return;
        }

        const direction =
            params.direction ?? UISortablePlugin.Direction.Descending;
        const th = Array.from(
            this.target.querySelectorAll<HTMLElement>('th.sort')
        ).find((el) => el.dataset.sortColumn === params.field);

        if (!th?.parentElement) {
            return;
        }

        th.parentElement
            .querySelectorAll('th.sort')
            .forEach((node) =>
                this.updateHeaders(
                    node as HTMLTableCellElement,
                    th as HTMLTableCellElement,
                    direction
                )
            );
    }

    /**
     * @returns {UISortablePluginSortParams}
     */
    getSortParams(): UISortablePluginSortParams {
        let th = this.target.querySelector<HTMLElement>('th.sort.-sorted');
        if (!th) {
            th = this.target.querySelector('th.sort');
        }
        if (!th) {
            return {
                field: null,
                direction: UISortablePlugin.Direction.Ascending,
            };
        }
        return {
            field: th.dataset.sortColumn ?? null,
            direction: th.classList.contains('-desc')
                ? UISortablePlugin.Direction.Descending
                : UISortablePlugin.Direction.Ascending,
        };
    }

    /**
     * Fires callback when target table got a click inside
     * @param {Event} event
     * @private
     */
    private handleClickEvent = (event: Event): void => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const eventHolder = closestEventHolder(target, this.target);
        if (
            eventHolder instanceof HTMLTableCellElement &&
            eventHolder.dataset.event === 'sort'
        ) {
            event.preventDefault();
            this.sortByColumn(eventHolder);
        }
    };

    /**
     * @param {HTMLTableCellElement} headerCell
     * @param {HTMLTableCellElement} currentHeaderCell
     * @param {number} direction
     * @private
     */
    private updateHeaders(
        headerCell: HTMLTableCellElement,
        currentHeaderCell: HTMLTableCellElement,
        direction: number
    ): void {
        const isSorted = headerCell === currentHeaderCell;
        const isDesc =
            isSorted && direction === UISortablePlugin.Direction.Descending;
        const isAsc =
            !isSorted || direction === UISortablePlugin.Direction.Ascending;
        updateClassList(headerCell, {
            '-sorted': isSorted,
            '-desc': isDesc,
            '-asc': isAsc,
        });
        setAttributes(headerCell, {
            'aria-sort': !isSorted
                ? 'none'
                : isAsc
                  ? 'ascending'
                  : 'descending',
        });
    }

    /**
     * @inheritDoc
     */
    render() {}

    /**
     * @inheritDoc
     */
    hydrate() {
        const thead = this.target.querySelector('thead');
        thead?.addEventListener('click', this.handleClickEvent);
    }
}

/**
 *
 * @memberof UISortablePlugin
 * @event sort-table
 * @alias event:sort-table
 * @type {object}
 * @property {number} direction - 1 or -1
 * @property {string} column - column name
 * @augments {CustomEvent}
 */
